// ─── Quick Style Swap ─────────────────────────────────────────
// Curated editorial presets that instantly restyle the entire
// invitation without changing layout. Each preset bundles
// palette + fontPair + optional themeExtensions.

import React from 'react';
import { Check } from 'lucide-react';

export const STYLE_PRESETS = [
  {
    id: 'warm-editorial',
    name: 'Warm Editorial',
    palette: 'warm-paper',
    fontPair: 'cormorant-manrope',
    themeExtensions: {
      monoFont: '"Special Elite", "Courier New", monospace',
      handFont: '"Caveat", cursive',
      eastAsianFont: '"Noto Serif SC", serif',
    },
    preview: {
      bg: '#f2ece6',
      accent: '#9b7f6b',
      text: '#221c18',
      surface: '#fffaf6',
    },
  },
  {
    id: 'rose-romantic',
    name: 'Rose Romantic',
    palette: 'rose-beige',
    fontPair: 'cormorant-manrope',
    themeExtensions: {
      monoFont: '"Courier Prime", "Courier New", monospace',
      handFont: '"Caveat", cursive',
      eastAsianFont: '"Noto Serif SC", serif',
    },
    preview: {
      bg: '#F6EFEC',
      accent: '#C88F8F',
      text: '#2A2220',
      surface: '#FFF8F6',
    },
  },
  {
    id: 'forest-natural',
    name: 'Forest Natural',
    palette: 'forest-cream',
    fontPair: 'dmserif-plusjakarta',
    themeExtensions: {
      monoFont: '"JetBrains Mono", monospace',
      handFont: '"Caveat", cursive',
      eastAsianFont: '"Noto Serif SC", serif',
    },
    preview: {
      bg: '#F5F3EC',
      accent: '#556B5D',
      text: '#1C241F',
      surface: '#FFFDF8',
    },
  },
  {
    id: 'ivory-classic',
    name: 'Ivory Classic',
    palette: 'ivory-gold',
    fontPair: 'playfair-inter',
    themeExtensions: {
      monoFont: '"Courier Prime", "Courier New", monospace',
      handFont: '"Caveat", cursive',
      eastAsianFont: '"Noto Serif SC", serif',
    },
    preview: {
      bg: '#F8F4EC',
      accent: '#C8A96B',
      text: '#1F1A17',
      surface: '#FFFDF8',
    },
  },
  {
    id: 'dark-luxe',
    name: 'Dark Luxe',
    palette: 'black-champagne',
    fontPair: 'playfair-inter',
    themeExtensions: {
      monoFont: '"JetBrains Mono", monospace',
      handFont: '"Caveat", cursive',
      eastAsianFont: '"Noto Serif SC", serif',
      paper: '#1A1A1A',
      paperDark: '#111111',
    },
    preview: {
      bg: '#111111',
      accent: '#D4B06A',
      text: '#F6F1E8',
      surface: '#1A1A1A',
    },
  },
  {
    id: 'noir-gold',
    name: 'Noir Gold',
    palette: 'noir-gold',
    fontPair: 'cormorant-manrope',
    themeExtensions: {
      monoFont: '"Special Elite", "Courier New", monospace',
      handFont: '"Caveat", cursive',
      eastAsianFont: '"Noto Serif SC", serif',
      paper: '#1A1A1A',
      paperDark: '#0d0d0d',
    },
    preview: {
      bg: '#000000',
      accent: '#d4af37',
      text: '#ffffff',
      surface: '#1A1A1A',
    },
  },
];

export default function StyleSwapBar({ currentPresetId, onSelect }) {
  const activeId = currentPresetId || 'warm-editorial';

  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
      data-testid="style-swap-bar"
    >
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
        <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
          Quick Style
        </h3>
      </div>

      <div className="p-3 grid grid-cols-3 gap-2">
        {STYLE_PRESETS.map(preset => {
          const isActive = activeId === preset.id;

          return (
            <button
              key={preset.id}
              onClick={() => onSelect(preset)}
              className={`relative p-2 rounded-lg border-2 transition-all text-left group ${
                isActive
                  ? 'border-blue-500 ring-1 ring-blue-200 dark:ring-blue-800'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
              data-testid={`style-preset-${preset.id}`}
            >
              {/* Color swatch strip */}
              <div className="flex gap-1 mb-1.5">
                <div className="w-4 h-4 rounded-full border border-black/10" style={{ background: preset.preview.bg }} />
                <div className="w-4 h-4 rounded-full border border-black/10" style={{ background: preset.preview.accent }} />
                <div className="w-4 h-4 rounded-full border border-black/10" style={{ background: preset.preview.text }} />
              </div>

              {/* Name */}
              <div className="text-[10px] font-medium text-gray-600 dark:text-gray-400 truncate leading-tight">
                {preset.name}
              </div>

              {/* Selected check */}
              {isActive && (
                <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
